type TemporalAmountFns<TUnit extends string> = Record<TUnit, (date: Date, amount: number) => Date>;
type TemporalDiffFns<TUnit extends string> = Record<TUnit, (end: Date, start: Date) => number>;
type TemporalBoundaryFns<TUnit extends string> = Record<TUnit, (date: Date) => Date>;

function requireTemporalFn<TFn>(unit: string, fn: TFn | undefined): TFn {
  if (!fn) {
    throw new Error(`Invalid unit: ${unit}`);
  }

  return fn;
}

export function applyTemporalUnit<TUnit extends string>(
  date: Date,
  amount: number,
  unit: TUnit,
  fns: TemporalAmountFns<TUnit>
): Date {
  return requireTemporalFn(unit, fns[unit])(date, amount);
}

export function diffTemporalUnit<TUnit extends string>(
  start: Date,
  end: Date,
  unit: TUnit,
  fns: TemporalDiffFns<TUnit>
): number {
  // Args are intentionally forwarded as (end, start): the diff fns are date-fns
  // `differenceIn*`, which take (laterDate, earlierDate) and return later − earlier.
  // Passing (end, start) yields a positive diff when `end` is after `start`.
  return requireTemporalFn(unit, fns[unit])(end, start);
}

export function applyTemporalBoundary<TUnit extends string>(
  date: Date,
  unit: TUnit,
  fns: TemporalBoundaryFns<TUnit>
): Date {
  return requireTemporalFn(unit, fns[unit])(date);
}
